import React, { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

/* Pull down at the top of a page to reload it — on phones and tablets, in
   the browser and in the installed app alike.

   Neither had it. An installed PWA gets no browser pull-to-refresh at all,
   and index.css sets `overscroll-behavior-y: contain` on the body (to stop
   the rubber-band bounce that makes an app feel like a web page), which
   switches it off in an ordinary mobile tab too. The admin pages also
   scroll inside <main> rather than the document, where the browser's own
   gesture never looks.

   One listener for the whole app. The scroller is found from where the
   finger lands, so the admin <main> and the public pages' document both
   work; a pull only begins when that scroller is already at its top.

   Stays out of the way of every other touch interaction:
   - anything in a dialog, sheet or other fixed layer (the photo cropper
     drags and pinches inside one)
   - elements that claim their own gestures with touch-action: none
   - nested scroll areas (a notification list, a long table) that are not
     at their own top
   - sideways swipes, and any second finger (a pinch)
   Reload rather than re-fetching per page: it's what the gesture means in
   every browser, and it is correct for every screen without each page
   having to opt in. */
const THRESHOLD = 70;   // px of (dampened) pull that commits to a reload
const MAX_PULL  = 110;

function scrollerFor(target) {
  for (let n = target; n && n !== document.body; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if ((cs.overflowY === "auto" || cs.overflowY === "scroll") && n.scrollHeight > n.clientHeight) return n;
  }
  /* Outside any scroll area — e.g. the admin header. "At the top" must then
     mean the page's real content, which in the admin app is <main>, not the
     document; otherwise a pull on the header reloads a page scrolled far
     down. */
  const main = document.querySelector("main");
  if (main && main.scrollHeight > main.clientHeight) return main;
  return document.scrollingElement || document.documentElement;
}

function claimedElsewhere(target) {
  for (let n = target; n && n !== document.body; n = n.parentElement) {
    if (n.matches?.('[role="dialog"], [aria-modal="true"], [data-no-pull], input, textarea, select')) return true;
    const cs = getComputedStyle(n);
    if (cs.position === "fixed" || cs.touchAction === "none") return true;
  }
  return false;
}

export default function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const gesture = useRef(null);
  const pullRef = useRef(0);

  useEffect(() => {
    if (!window.matchMedia?.("(pointer: coarse)").matches) return;   // touch devices only

    const set = v => { pullRef.current = v; setPull(v); };

    const onStart = e => {
      gesture.current = null;
      if (refreshing || e.touches.length !== 1 || claimedElsewhere(e.target)) return;
      const scroller = scrollerFor(e.target);
      const isPage = scroller === document.scrollingElement || scroller === document.documentElement || scroller.tagName === "MAIN";
      if (!isPage || scroller.scrollTop > 0) return;   // only the page itself, and only at its top
      gesture.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, scroller, active: false };
    };

    const onMove = e => {
      const g = gesture.current;
      if (!g) return;
      if (e.touches.length !== 1) { gesture.current = null; set(0); return; }   // a pinch took over
      const dx = e.touches[0].clientX - g.x, dy = e.touches[0].clientY - g.y;
      if (!g.active) {
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) { gesture.current = null; return; }  // sideways swipe
        if (dy < -4 || g.scroller.scrollTop > 0) { gesture.current = null; return; }                // scrolling, not pulling
        if (dy < 8) return;
        g.active = true;
      }
      if (e.cancelable) e.preventDefault();   // hold the page still while pulling
      set(Math.min(MAX_PULL, Math.max(0, (dy - 8) * 0.5)));
    };

    const onEnd = () => {
      const g = gesture.current;
      gesture.current = null;
      if (!g?.active) return;
      if (pullRef.current >= THRESHOLD) {
        setRefreshing(true);
        set(THRESHOLD * 0.75);
        // One frame so the spinner is seen before the page goes.
        setTimeout(() => window.location.reload(), 150);
      } else {
        set(0);
      }
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [refreshing]);

  if (!pull && !refreshing) return null;
  const progress = Math.min(1, pull / THRESHOLD);
  return (
    <div aria-hidden={!refreshing} role={refreshing ? "status" : undefined}
      className="pointer-events-none fixed left-1/2 z-[46] flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg ring-1 ring-black/5"
      style={{
        top: "calc(env(safe-area-inset-top, 0px) + 8px)",
        transform: `translate(-50%, ${pull - 8}px)`,
        opacity: refreshing ? 1 : 0.35 + progress * 0.65,
        transition: gesture.current ? "none" : "transform 200ms ease, opacity 200ms ease",
      }}>
      <RefreshCw size={18}
        className={`${progress >= 1 || refreshing ? "text-amber-500" : "text-gray-400"} ${refreshing ? "animate-spin" : ""}`}
        style={refreshing ? undefined : { transform: `rotate(${progress * 300}deg)` }} />
      {refreshing && <span className="sr-only">Refreshing</span>}
    </div>
  );
}
